/**
 * Live indicator component that displays a flashing red dot and "LIVE" label.
 * Used in the transport bar for live streams. Only visible when playing.
 * Supports preview mode with yellow dot, faster flash, and "Preview" label.
 */

import { t } from 'media-chrome/utils/i18n.js';
import {
  MediaStateReceiverAttributes,
  MediaUIAttributes,
} from 'media-chrome/constants.js';

interface MediaControllerElement extends HTMLElement {
  associateElement?: (el: HTMLElement) => void;
  unassociateElement?: (el: HTMLElement) => void;
}

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 0 8px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #fff;
      user-select: none;
    }

    :host([mediapaused]) {
      display: none;
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #e53935;
      animation: pulse 2s ease-in-out infinite;
    }

    :host([preview]) .dot {
      background-color: #ffc107;
      animation: pulse-fast 0.8s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.4;
      }
    }

    @keyframes pulse-fast {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.3;
      }
    }

    .label {
      line-height: 1;
    }
  </style>
  <span class="dot"></span>
  <span class="label">LIVE</span>
`;

class BunnyLiveIndicator extends HTMLElement {
  #mediaController: MediaControllerElement | null = null;

  static get observedAttributes(): string[] {
    return [
      MediaStateReceiverAttributes.MEDIA_CONTROLLER,
      MediaUIAttributes.MEDIA_PAUSED,
      'preview',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));
  }

  attributeChangedCallback(
    attrName: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (
      attrName === MediaStateReceiverAttributes.MEDIA_CONTROLLER &&
      oldValue !== newValue
    ) {
      if (oldValue) {
        this.#mediaController?.unassociateElement?.(this);
        this.#mediaController = null;
      }
      if (newValue && this.isConnected) {
        this.#mediaController = (this.getRootNode() as Document).getElementById(
          newValue,
        ) as MediaControllerElement | null;
        this.#mediaController?.associateElement?.(this);
      }
    }

    if (attrName === 'preview') {
      this.#updateLabel();
    }
  }

  connectedCallback() {
    this.#updateLabel();

    const mediaControllerId = this.getAttribute(
      MediaStateReceiverAttributes.MEDIA_CONTROLLER,
    );
    if (mediaControllerId) {
      this.#mediaController = (this.getRootNode() as Document).getElementById(
        mediaControllerId,
      ) as MediaControllerElement | null;
      this.#mediaController?.associateElement?.(this);
    }
  }

  disconnectedCallback() {
    this.#mediaController?.unassociateElement?.(this);
    this.#mediaController = null;
  }

  #updateLabel() {
    const label = this.shadowRoot!.querySelector('.label');
    if (label) {
      const isPreview = this.hasAttribute('preview');
      const translate = t as (key: string) => string;
      label.textContent = isPreview ? translate('preview') : translate('live');
    }
  }
}

if (!globalThis.customElements.get('bunny-live-indicator')) {
  globalThis.customElements.define('bunny-live-indicator', BunnyLiveIndicator);
}

export { BunnyLiveIndicator };
